import { useEffect } from 'react';
import { useHistory, useLocation, Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ROUTES } from 'features/routes';
import { useAppSelector } from 'store/hooks';
import { useToastNotification } from 'hooks/useToastNotification';
import { Stepper } from './components/Stepper';
import { CommitStep } from './components/steps/CommitStep';
import { ColumnMappingStep } from './components/steps/ColumnMappingStep';
import { EventMappingStep } from './components/steps/EventMappingStep';
import { OptionsStep } from './components/steps/OptionsStep';
import { PreviewStep } from './components/steps/PreviewStep';
import { ScopeStep } from './components/steps/ScopeStep';
import { UploadStep } from './components/steps/UploadStep';
import { ValueMappingStep } from './components/steps/ValueMappingStep';
import { useDeleteTicketImport } from './hooks';
import { WizardProvider, useWizard } from './store';
import { Button, Page, Subtitle, Title } from './TicketImporter.styles';

const Inner = (): JSX.Element => {
	const history = useHistory();
	const { pathname, hash } = useLocation();
	const queryClient = useQueryClient();
	const { state, dispatch } = useWizard();
	const currentPartner = useAppSelector((s) => s.auth.loggedUser?.currentPartner);
	const { showNotification, TYPES } = useToastNotification();

	const { mutate: discard } = useDeleteTicketImport({
		onSuccess: () => {
			showNotification({ type: TYPES.SUCCESS, message: 'Import discarded' });
			dispatch({ type: 'reset' });
			history.replace(`${pathname}#1`);
		},
		onError: (err: any) => {
			const msg = err?.response?.data?.error || 'Could not discard import';
			showNotification({ type: TYPES.ERROR, message: msg });
		},
		onSettled: () => {
			queryClient.refetchQueries({ queryKey: ['ticket-imports'] });
		},
	});

	const onDiscard = () => {
		if (!state.importId) return;
		if (!window.confirm('Discard this import? Any saved mappings will be lost.')) return;
		discard(state.importId);
	};

	// Don't offer Discard from the upload step (nothing to discard yet) or
	// from the final success state of commit.
	const showDiscard = state.importId !== null && hash !== '#1' && hash !== '#8';

	useEffect(() => {
		if (!hash) {
			history.replace(`${pathname}#1`);
		}
	}, [hash, history, pathname]);

	// If the wizard's local state was cleared (e.g. user reloaded the page),
	// kick them back to step 1 instead of letting later steps render with null importId.
	useEffect(() => {
		if (hash && hash !== '#1' && !state.importId) {
			history.replace(`${pathname}#1`);
		}
	}, [hash, history, pathname, state.importId]);

	return (
		<Page>
			<div
				style={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					marginBottom: 12,
					flexWrap: 'wrap',
					gap: 12,
				}}
			>
				<div
					style={{
						display: 'inline-flex',
						alignItems: 'center',
						gap: 10,
						padding: '6px 12px',
						background: '#eef2ff',
						border: '1px solid #c7d2fe',
						borderRadius: 999,
						fontSize: 13,
						color: '#312e81',
					}}
				>
					<span style={{ fontWeight: 600 }}>Importing into:</span>
					<span>{currentPartner?.name || 'No partner selected'}</span>
					{currentPartner ? <small style={{ color: '#6366f1' }}>(#{currentPartner.ID})</small> : null}
					<Link
						to={ROUTES.SWITCH_TEAM_PATH}
						style={{ color: '#6366f1', textDecoration: 'underline', fontSize: 12, marginLeft: 4 }}
					>
						Change
					</Link>
				</div>
				{showDiscard && (
					<Button variant="ghost" onClick={onDiscard} style={{ color: '#dc2626' }}>
						Discard import
					</Button>
				)}
			</div>
			<Title>Ticket Importer</Title>
			<Subtitle>
				Import tickets sold on another platform into Tixologi. Tickets are recorded as <code>external</code>{' '}
				transactions and are excluded from payout.
			</Subtitle>
			<Stepper />
			{(hash === '' || hash === '#1') && <UploadStep />}
			{hash === '#2' && <ScopeStep />}
			{hash === '#3' && <ColumnMappingStep />}
			{hash === '#4' && <EventMappingStep />}
			{hash === '#5' && <OptionsStep />}
			{hash === '#6' && <ValueMappingStep />}
			{hash === '#7' && <PreviewStep />}
			{hash === '#8' && <CommitStep />}
		</Page>
	);
};

export const TicketImporter = (): JSX.Element => (
	<WizardProvider>
		<Inner />
	</WizardProvider>
);
